import React, {useEffect, useMemo} from 'react';
import {ContentPasteOffRounded as ContentPasteOffRoundedIcon} from '@mui/icons-material';
import AdmicityTableV2 from './Table/V2/AdmicityTableV2';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import AdmicityStatus from './AdmicityStatus';
import {getFormRequestStatusInfo} from '../pages/SchoolDashboard/RequestAndResponses/common/common';
import {formatDate} from '../utility/dateUtil';
import PropTypes from 'prop-types';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import {getApprovalStatusInfo} from '../constants/FormApprovalStatuses';
import Tooltip from '@mui/material/Tooltip';

const getByPath = (obj, path) =>
    path?.split('.').reduce((acc, k) => (acc == null ? acc : acc[k]), obj);

const TruncatedText = ({value, maxWidth = 220}) => (
    <Tooltip title={value ?? ''} disableInteractive>
        <Box
            sx={{
                maxWidth,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 0,
            }}
        >
            {value ?? ''}
        </Box>
    </Tooltip>
);

TruncatedText.propTypes = {
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    maxWidth: PropTypes.number,
};

const buildFormRequestsColumns = (hasApprovals) => {
    const base = [
        {
            header: 'Status',
            accessor: 'status',
            template: hasApprovals ? 'minmax(160px, 1fr)' : '1fr',
            cell: (row) => <AdmicityStatus {...getFormRequestStatusInfo(row.status)} />,
        },
        {
            header: 'Submitted Date',
            accessor: 'editedAt',
            template: hasApprovals ? 'minmax(160px, 1fr)' : '1fr',
            cell: (row) => (row.editedAt ? formatDate(row.editedAt) : ''),
        },
        {
            header: 'Submitted By',
            accessor: 'editedBy.fullname',
            template: hasApprovals ? 'minmax(160px, 1fr)' : '1fr',
            cell: (row) => (
                <TruncatedText value={row?.editedBy?.fullname}/>
            ),
        },
    ];

    if (hasApprovals) {
        base.push(
            {
                header: 'Approval Stage',
                accessorKey: 'approvalStage',
                template: 'minmax(160px, 1fr)',
                cell: (row) => <TruncatedText value={row.approvalStage}/>,
            },
            {
                header: 'Approval Progress',
                accessorKey: 'approvalStageProgress',
                template: 'minmax(160px, 1fr)',
            },
            {
                header: 'Approval Status',
                accessorKey: 'approvalStatus',
                template: 'minmax(160px, 1fr)',
                cell: (row) =>
                    row.approvalStatus
                        ? (
                            <AdmicityStatus {...getApprovalStatusInfo(row.approvalStatus)} />
                        )
                        : (
                            ''
                        ),
            },
            {
                header: 'Approved By',
                accessorKey: 'approvedBy',
                template: 'minmax(160px, 1fr)',
                cell: (row) => (
                    <TruncatedText value={row.approvedBy}/>
                ),
            },
            {
                header: 'Approved At',
                accessorKey: 'approvedAt',
                template: 'minmax(160px, 1fr)',
                cell: (row) => (row.approvedAt ? formatDate(row.approvedAt) : ''),
            },
            {
                header: 'Approval Note',
                accessorKey: 'approvalNote',
                template: 'minmax(160px, 1fr)',
                cell: (row) => <TruncatedText value={row.approvalNote} maxWidth={360}/>,
            }
        );
    }

    return base;
};

const FormRequestsPanel = ({formId, personId, navigate, fetchFn}) => {
    const [getFormResponses, {data: responses = [], isLoading}] = fetchFn();

    useEffect(() => {
        getFormResponses({formId, personId});
    }, [formId, personId, getFormResponses]);

    const hasApprovals = useMemo(
        () => Array.isArray(responses) && responses.some((r) => r?.hasApproval),
        [responses]
    );

    const {columns, gridTemplateColumns} = useMemo(() => {
        const cols = buildFormRequestsColumns(hasApprovals);
        const template = hasApprovals
            ? cols.map((c) => c.template || 'max-content').join(' ')
            : `repeat(${cols.length}, minmax(0, 1fr))`;
        return {columns: cols, gridTemplateColumns: template};
    }, [hasApprovals]);

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" width="100%">
                <CircularProgress size={20}/>
            </Box>
        );
    }

    if (!responses || responses.length === 0) {
        return (
            <Typography color="text.secondary" width="100%" textAlign="center">
                No submissions yet
            </Typography>
        );
    }

    const renderCell = (col, row) => {
        let content = '';
        if (col.cell) content = col.cell(row);
        else if (col.accessor) content = getByPath(row, col.accessor) ?? '';
        else if (col.accessorKey) content = row[col.accessorKey] ?? '';

        return (
            <Box key={col.header} sx={{minWidth: 0}}>
                {content}
            </Box>
        );
    };

    return (
        <Box onClick={(e) => e.stopPropagation()} sx={{cursor: 'default'}}>
            <Typography gutterBottom fontWeight={600}>
                Submissions
            </Typography>

            <Paper variant="outlined" sx={{overflowX: hasApprovals ? 'auto' : 'hidden'}}>
                <Box
                    key={`hdr-${columns.length}`}
                    sx={{
                        display: 'grid',
                        gridTemplateColumns,
                        gap: 2,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        typography: 'subtitle2',
                        px: 1,
                        py: 2,
                        backgroundColor: 'rgba(0,0,0,0.02)',
                        fontWeight: 600,
                        minWidth: hasApprovals ? 'fit-content' : 'auto',
                    }}
                >
                    {columns.map((col) => (
                        <Box key={col.header} sx={{minWidth: 0}}>
                            {col.header}
                        </Box>
                    ))}
                </Box>

                {responses.map((row) => (
                    <Box
                        key={`${row.id}-${columns.length}`}
                        sx={{
                            display: 'grid',
                            gridTemplateColumns,
                            gap: 2,
                            px: 1,
                            py: 2,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            '&:last-of-type': {border: 'none'},
                            ':hover': {backgroundColor: 'action.hover', cursor: 'pointer'},
                            minWidth: hasApprovals ? 'fit-content' : 'auto',
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`${row.id}`);
                        }}
                    >
                        {columns.map((col) => renderCell(col, row))}
                    </Box>
                ))}
            </Paper>
        </Box>
    );
};

FormRequestsPanel.propTypes = {
    formId: PropTypes.number.isRequired,
    personId: PropTypes.number.isRequired,
    navigate: PropTypes.func.isRequired,
    fetchFn: PropTypes.func.isRequired,
};

const NotificationFormsTable = ({
                                    id = 'notification-forms',
                                    data,
                                    columns,
                                    isLoading,
                                    selectedRowIndex,
                                    setSelectedRowIndex,
                                    navigate,
                                    personId,
                                    fetchResponsesFn,
                                }) => {
    return (
        <AdmicityTableV2
            variant="elevation"
            id={id}
            columns={columns}
            data={data}
            isLoading={isLoading}
            initialState={{
                expanded: selectedRowIndex !== undefined ? {[selectedRowIndex]: true} : {},
                columnFilters: [],
            }}
            tableProps={{
                enableSorting: false,
                layoutMode: 'grid-no-grow',
                enableColumnResizing: false,
                enableRowActions: true,
                enableBottomToolbar: false,
                enableTopToolbar: false,
                manualFiltering: false,
                enablePagination: false,
                manualSorting: false,
                enableHiding: false,
                showGlobalFilter: true,
                enableExpandAll: false,
                muiExpandButtonProps: ({row, table}) => ({
                    onClick: () => {
                        const isExpanded = row.getIsExpanded();
                        if (!isExpanded) setSelectedRowIndex(row.index);
                        table.setExpanded({[row.id]: !isExpanded});
                    },
                }),
                muiDetailPanelProps: {sx: {'& .MuiCollapse-root': {width: '100%'}}},
                renderDetailPanel: ({row}) => (
                    <FormRequestsPanel
                        formId={row.original.formId}
                        personId={personId}
                        navigate={navigate}
                        fetchFn={fetchResponsesFn}
                    />
                ),
            }}
            onRowClick={(params) => navigate(`general/${params.formId}`)}
            rowActions={[{label: 'Submit', action: (params) => navigate(`general/${params.formId}`)}]}
            noRowsOverlay={{icon: <ContentPasteOffRoundedIcon/>, text: 'No forms available'}}
        />
    );
};

NotificationFormsTable.propTypes = {
    id: PropTypes.string,
    data: PropTypes.array.isRequired,
    columns: PropTypes.array.isRequired,
    isLoading: PropTypes.bool,
    selectedRowIndex: PropTypes.number,
    setSelectedRowIndex: PropTypes.func.isRequired,
    navigate: PropTypes.func.isRequired,
    personId: PropTypes.number.isRequired,
    fetchResponsesFn: PropTypes.func.isRequired,
};

export default NotificationFormsTable;
